import React, { useState, useEffect } from 'react';

// 真实证书数据（按含金量倒序：云 & AI > 项目管理 > 安全 > 基础）
interface Cert {
  key: string;
  name: string;              // 中文短名（卡面主标）
  fullName: string;          // 英文全称
  issuer: string;            // 颁发机构短名
  issuerFull: string;        // 颁发机构全称
  category: 'cloud' | 'ai' | 'security' | 'pm' | 'basic';
  categoryLabel: string;
  issueDate: string;         // YYYY.MM
  expireDate?: string;       // YYYY.MM · 空表示终身
  certId: string;
  image: string;             // /certs/xxx.png
  icon: string;              // fa icon
}

const CERTS: Cert[] = [
  {
    key: 'acp-llm',
    name: '阿里云 ACP · 大模型',
    fullName: 'Alibaba Cloud Certified Professional — LLM',
    issuer: 'Alibaba Cloud',
    issuerFull: '阿里云 · 大语言模型方向',
    category: 'ai',
    categoryLabel: 'AI / 大模型',
    issueDate: '2025.06',
    expireDate: '2028.06',
    certId: 'ACP26260602699484',
    image: '/certs/acp-llm.png',
    icon: 'fa-brain',
  },
  {
    key: 'acp-cloud',
    name: '阿里云 ACP · 云计算',
    fullName: 'Alibaba Cloud Certified Professional — Cloud Computing',
    issuer: 'Alibaba Cloud',
    issuerFull: '阿里云 · 云计算方向',
    category: 'cloud',
    categoryLabel: '云计算',
    issueDate: '2025.07',
    expireDate: '2027.07',
    certId: 'ACP01250702409332',
    image: '/certs/acp-cloud.jpg',
    icon: 'fa-cloud',
  },
  {
    key: 'pmp',
    name: 'PMP · 项目管理',
    fullName: 'Project Management Professional',
    issuer: 'PMI',
    issuerFull: 'Project Management Institute',
    category: 'pm',
    categoryLabel: '项目管理',
    issueDate: '2024.06',
    expireDate: '2027.06',
    certId: '3865368',
    image: '/certs/pmp.png',
    icon: 'fa-diagram-project',
  },
  {
    key: 'cztp',
    name: 'CZTP · 零信任',
    fullName: 'Certified Zero Trust Professional',
    issuer: 'CSA',
    issuerFull: 'Cloud Security Alliance',
    category: 'security',
    categoryLabel: '网络安全',
    issueDate: '2022.08',
    expireDate: '2025.08',
    certId: 'CSA0858700820',
    image: '/certs/cztp.png',
    icon: 'fa-shield-halved',
  },
  {
    key: 'ncre-c',
    name: '计算机二级 · C 语言',
    fullName: 'National Computer Rank Examination — Level 2 (C)',
    issuer: '教育部',
    issuerFull: '教育部考试中心',
    category: 'basic',
    categoryLabel: '基础能力',
    issueDate: '2018.09',
    certId: '2452210129537',
    image: '/certs/ncre-c.png',
    icon: 'fa-code',
  },
  {
    key: 'cet6',
    name: '大学英语六级',
    fullName: 'College English Test — Band 6',
    issuer: '教育部',
    issuerFull: '教育部 · 全国大学英语四六级考试委员会',
    category: 'basic',
    categoryLabel: '语言能力',
    issueDate: '2019.06',
    certId: '210060191229228',
    image: '/certs/cet6.jpg',
    icon: 'fa-language',
  },
];

/* ---------- 大图 Modal ---------- */
const CertModal: React.FC<{ cert: Cert; onClose: () => void }> = ({ cert, onClose }) => {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', h);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="cert-overlay" onClick={onClose}>
      <div className={`cert-modal cert-modal--${cert.category}`} onClick={(e) => e.stopPropagation()}>
        <button className="cert-modal-close" onClick={onClose} aria-label="关闭">
          <i className="fa-solid fa-xmark" />
        </button>
        <div className="cert-modal-image-wrap">
          <img src={cert.image} alt={cert.name} className="cert-modal-image" />
        </div>
        <div className="cert-modal-meta">
          <div className="cert-modal-tag">
            <i className={`fa-solid ${cert.icon}`} />
            {cert.categoryLabel}
          </div>
          <h3 className="cert-modal-title">{cert.name}</h3>
          <div className="cert-modal-sub">{cert.fullName}</div>
          <dl className="cert-modal-dl">
            <div>
              <dt>颁发机构</dt>
              <dd>{cert.issuerFull}</dd>
            </div>
            <div>
              <dt>颁发时间</dt>
              <dd>{cert.issueDate}</dd>
            </div>
            {cert.expireDate && (
              <div>
                <dt>有效期至</dt>
                <dd>{cert.expireDate}</dd>
              </div>
            )}
            <div>
              <dt>证书编号</dt>
              <dd className="cert-modal-id">{cert.certId}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

/* ---------- 页脚主组件 ---------- */
export const SiteFooter: React.FC = () => {
  const [activeCert, setActiveCert] = useState<Cert | null>(null);

  return (
    <footer className="foot-section" data-testid="page-footer">
      <div className="foot-inner">
        {/* 统一三段式头部 —— 与 GTM / 能力图谱 保持一致 */}
        <header className="foot-header">
          <div className="foot-eyebrow">CERTIFICATIONS · HONORS</div>
          <h2 className="foot-title">认证与荣誉</h2>
          <p className="foot-subtitle">
            6 项云 &amp; AI · 项目管理 · 安全类专业认证 · 点击卡片查看证书原图
          </p>
        </header>

        {/* 证书墙 */}
        <div className="foot-certs-block">
          <div className="foot-certs-grid">
            {CERTS.map((c) => (
              <button
                key={c.key}
                className={`foot-cert foot-cert--${c.category}`}
                onClick={() => setActiveCert(c)}
                type="button"
              >
                <div className="foot-cert-thumb">
                  <img src={c.image} alt={c.name} loading="lazy" />
                  <span className="foot-cert-thumb-badge">
                    <i className={`fa-solid ${c.icon}`} />
                  </span>
                </div>
                <div className="foot-cert-body">
                  <div className="foot-cert-cat">{c.categoryLabel}</div>
                  <div className="foot-cert-name">{c.name}</div>
                  <div className="foot-cert-issuer">
                    {c.issuer}
                    <span className="foot-cert-year">· {c.issueDate.slice(0, 4)}</span>
                  </div>
                </div>
                <i className="fa-solid fa-up-right-and-down-left-from-center foot-cert-expand" />
              </button>
            ))}
          </div>
        </div>

        {/* 弹性 spacer —— 把联系方式推到页面底部 */}
        <div className="foot-spacer" aria-hidden="true" />

        {/* 联系方式区块 */}
        <div className="foot-contact-block">
          <div className="foot-contact-left">
            <div className="foot-contact-eyebrow">LET'S TALK</div>
            <h3>让我们聊聊 · Cloud × AI SA 岗位</h3>
            <p>
              目前正在寻找 <strong>云 / AI 解决方案架构师</strong> 类岗位 · 深圳，可短出差
            </p>
          </div>
          <div className="foot-contact-right">
            <a className="foot-contact-item" href="mailto:970354314@qq.com">
              <i className="fa-solid fa-envelope" />
              <span className="foot-contact-label">邮箱</span>
              970354314@qq.com
            </a>
            <a className="foot-contact-item" href="tel:+8617824033403">
              <i className="fa-solid fa-mobile-screen-button" />
              <span className="foot-contact-label">微信 / 电话</span>
              178&nbsp;2403&nbsp;3403
            </a>
            <a className="foot-contact-item" href="https://github.com/dongjun-dj" target="_blank" rel="noreferrer">
              <i className="fa-brands fa-github" />
              <span className="foot-contact-label">GitHub</span>
              @dongjun-dj
            </a>
            <a className="foot-contact-item" href="/resume.pdf" target="_blank" rel="noreferrer">
              <i className="fa-solid fa-file-pdf" />
              <span className="foot-contact-label">简历</span>
              下载 PDF
            </a>
          </div>
        </div>

        {/* 版权 */}
        <div className="foot-copyright">
          <span>© 2026 董俊 · dongjun.tech · Cloud &amp; AI Solution Architect</span>
          <span>
            KA 大客户售前 · 云 / AI 方向求职中
            <a href="https://github.com/dongjun-dj/SQZS" target="_blank" rel="noreferrer">Site source</a>
          </span>
        </div>
      </div>

      {activeCert && <CertModal cert={activeCert} onClose={() => setActiveCert(null)} />}
    </footer>
  );
};

export default SiteFooter;
